import React, { useState, useEffect } from 'react';
import s from './ContactsPage.module.css';
import { ContactModal } from '../../components/ContactModal/ContactModal';
import { ReactComponent as EmailIcon } from './../../assets/icons/emailIcon.svg';
import { ReactComponent as PhoneIcon } from './../../assets/icons/phoneIcon.svg';
import { ReactComponent as GeoIcon } from './../../assets/icons/geoIcon.svg';
import { settingsApi } from '../../api/settings';
import { requestsApi } from '../../api/requests';

interface Contact {
    id: number;
    phone: string;
    name: string;
}

interface SettingsData {
    address: string;
    email: string;
    contacts: Contact[];
}

export const ContactsPage: React.FC = () => {
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [loading, setLoading] = useState(true);
    const [settings, setSettings] = useState<SettingsData>({
        address: '',
        email: '',
        contacts: [],
    });

    const [formData, setFormData] = useState({
        name: '',
        email: '',
        message: '',
        privacyAccepted: false,
        consentAccepted: false,
    });
    const [sending, setSending] = useState(false);
    const [submitError, setSubmitError] = useState<string | null>(null);
    const [submitSuccess, setSubmitSuccess] = useState(false);

    useEffect(() => {
        loadSettings();
    }, []);

    const loadSettings = async () => {
        try {
            setLoading(true);
            const data = await settingsApi.getAllPublic();

            const settingsMap: any = {};
            data.forEach(item => {
                settingsMap[item.key] = item.value;
            });

            let contacts: Contact[] = [];
            if (settingsMap.contacts) {
                try {
                    contacts = JSON.parse(settingsMap.contacts);
                } catch {
                    contacts = [];
                }
            }

            setSettings({
                address: settingsMap.office_address || 'Хабаровск, Калинина 120',
                email: settingsMap.contact_email || 'rdb-method@yandex.ru',
                contacts: contacts,
            });
        } catch (error) {
            console.error('Ошибка загрузки настроек:', error);
        } finally {
            setLoading(false);
        }
    };

    const handleFormChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
        const { name, value, type } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? (e.target as HTMLInputElement).checked : value,
        }));
    };

    const handleCheckboxChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const { name, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: checked,
        }));
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setSubmitError(null);
        setSubmitSuccess(false);

        if (!formData.name.trim()) {
            setSubmitError('Пожалуйста, введите ваше имя');
            return;
        }
        if (!formData.email.trim() || !formData.email.includes('@')) {
            setSubmitError('Пожалуйста, введите корректный email');
            return;
        }
        if (!formData.message.trim()) {
            setSubmitError('Пожалуйста, введите сообщение');
            return;
        }
        if (!formData.privacyAccepted) {
            setSubmitError('Пожалуйста, примите политику конфиденциальности');
            return;
        }
        if (!formData.consentAccepted) {
            setSubmitError('Пожалуйста, дайте согласие на обработку персональных данных');
            return;
        }

        try {
            setSending(true);

            await requestsApi.create({
                name: formData.name.trim(),
                email: formData.email.trim(),
                message: formData.message.trim(),
                phone: '',
            });

            setSubmitSuccess(true);
            setFormData({
                name: '',
                email: '',
                message: '',
                privacyAccepted: false,
                consentAccepted: false,
            });
            setTimeout(() => {
                setSubmitSuccess(false);
            }, 3000);
        } catch (error) {
            console.error('Ошибка отправки:', error);
            setSubmitError('Произошла ошибка при отправке. Пожалуйста, попробуйте позже.');
        } finally {
            setSending(false);
        }
    };

    if (loading) {
        return <div className={s.loading}>Загрузка...</div>;
    }

    return (
        <>
            <div className={s.contactsPage}>
                <div className={s.leftColumn}>
                    <h2 className={s.title}>Наши контакты</h2>
                    <div className={s.contactInfo}>
                        <div className={s.contactItem}>
                            <EmailIcon width={18} height={18} />
                            <div className={s.contactValue}>{settings.email}</div>
                        </div>

                        {settings.contacts.map((contact) => (
                            <div key={contact.id} className={s.contactItem}>
                                <PhoneIcon width={18} height={18} />
                                <a href={`tel:${contact.phone.replace(/[^0-9+]/g, '')}`} className={s.contactValue}>
                                    {contact.phone}
                                </a>
                                <div className={s.contactSub}>{contact.name}</div>
                            </div>
                        ))}

                        <div className={s.contactItem}>
                            <GeoIcon width={18} height={18} />
                            <div className={s.contactValue}>{settings.address}</div>
                        </div>
                    </div>
                </div>

                <div className={s.rightColumn}>
                    <div className={s.modalContent}>
                        {submitSuccess && (
                            <div className={s.successMessage}>
                                ✅ Ваше сообщение отправлено!
                            </div>
                        )}

                        {submitError && (
                            <div className={s.errorMessage}>
                                {submitError}
                            </div>
                        )}

                        <form className={s.form} onSubmit={handleSubmit}>
                            <div className={s.formGroup}>
                                <label className={s.label}>Имя *</label>
                                <input
                                    type="text"
                                    name="name"
                                    className={s.input}
                                    placeholder="Ваше имя"
                                    value={formData.name}
                                    onChange={handleFormChange}
                                    disabled={sending}
                                />
                            </div>
                            <div className={s.formGroup}>
                                <label className={s.label}>Email *</label>
                                <input
                                    type="email"
                                    name="email"
                                    className={s.input}
                                    placeholder="your@email.com"
                                    value={formData.email}
                                    onChange={handleFormChange}
                                    disabled={sending}
                                />
                            </div>
                            <div className={s.formGroup}>
                                <label className={s.label}>Сообщение *</label>
                                <textarea
                                    name="message"
                                    className={s.textarea}
                                    placeholder="Напишите ваше сообщение..."
                                    rows={4}
                                    value={formData.message}
                                    onChange={handleFormChange}
                                    disabled={sending}
                                />
                            </div>
                            <div className={s.checkboxGroup}>
                                <label className={s.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="privacyAccepted"
                                        className={s.checkbox}
                                        checked={formData.privacyAccepted}
                                        onChange={handleCheckboxChange}
                                        disabled={sending}
                                    />
                                    Я принимаю{' '}
                                    <span className={s.privacyLink}>политику конфиденциальности</span>
                                </label>
                            </div>
                            <div className={s.checkboxGroup}>
                                <label className={s.checkboxLabel}>
                                    <input
                                        type="checkbox"
                                        name="consentAccepted"
                                        className={s.checkbox}
                                        checked={formData.consentAccepted}
                                        onChange={handleCheckboxChange}
                                        disabled={sending}
                                    />
                                    Я даю{' '}
                                    <span className={s.privacyLink}>согласие на обработку персональных данных</span>
                                </label>
                            </div>
                            <button
                                type="submit"
                                className={s.submitButton}
                                disabled={sending}
                            >
                                {sending ? 'Отправка...' : 'Отправить'}
                            </button>
                        </form>
                    </div>
                </div>
            </div>
            <ContactModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
        </>
    );
};